import { Box, Button, Grid, Stack, Typography, styled, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import axios, { AxiosError } from "axios";
import { Fragment, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ApiRoutes } from "../configuration/apiRoutes";
import SEARCH_PARAMS from "../constants/searchParams";
import { LodResourceDataItemDto } from "../models/LodResourceDataItemDto";
import PageContent from "../parts/page-content/PageContent";
import { RestService } from "../utils/axios";
import { useTranslation } from "react-i18next";
import { Translations } from "../localization/translations";
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { LodResourceType } from "../models/LodResourceType";
import fileDownload from "js-file-download";
import useKlasisRouting from "../utils/klasisRouting";
import _ from "lodash";
import { useAppDispatch } from "../hooks/redux";
import { resetBreadcrumbState, setBreadcrumbLodResourceCode } from "../features/breadcrumbs";
import { LodResourceTemplateResponse } from "../models/LodResourceTemplateResponse";
import parse from "html-react-parser";

const Item = styled(Grid)(({ theme }) => ({
    wordWrap: "break-word",
    borderColor: theme.palette.divider,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    padding: "1rem 0"
}));

const HeaderItem = styled(Item)(() => ({
    fontWeight: "bold",
    marginBottom: "1rem"
}));

const DownloadFileBlock = styled(Box)(({ theme }) => ({
    paddingBottom: "1rem",
    marginTop: "1rem",
    borderColor: theme.palette.divider,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid'
}));

interface IRDFDownloadItem {
    title: string;
    resourceType: LodResourceType;
}

const LodResource: React.FC = () => {
    let { id, locale } = useParams();
    const search = useLocation().search;
    const { t } = useTranslation();
    const { klasisRoute } = useKlasisRouting();
    const appDispatch = useAppDispatch();

    const downloadItems: IRDFDownloadItem[] = [
        {
            resourceType: LodResourceType.jSONLD,
            title: t(Translations.lod.label.rdfFileType, {
                fileType: "JSON-LD"
            })
        },
        {
            resourceType: LodResourceType.cSV,
            title: t(Translations.lod.label.rdfFileType, {
                fileType: "CSV"
            })
        },
        {
            resourceType: LodResourceType.rDFa,
            title: t(Translations.lod.label.rdfFileType, {
                fileType: "RDFa"
            })
        },
        {
            resourceType: LodResourceType.xMLRDF,
            title: t(Translations.lod.label.rdfFileType, {
                fileType: "RDF/XML"
            })
        }
    ];

    const [data, setData] = useState<Array<LodResourceDataItemDto>>([]);
    const [templates, setTemplates] = useState<LodResourceTemplateResponse>();
    const [isLoading, setIsLoading] = useState(false);

    const searchParams = new URLSearchParams(search);
    const subject = searchParams.get(SEARCH_PARAMS.subject) ?? undefined;

    const resourceCode = _.split(subject, "/")[1] ?? undefined;

    useEffect(() => {
        setIsLoading(true);

        appDispatch(
            setBreadcrumbLodResourceCode(resourceCode ?? id)
        );

        RestService.Get<Array<LodResourceDataItemDto>>(
            ApiRoutes.Lod.ResourceContent({ 
                klasisCode: id ?? "", 
                localeCode: locale ?? "", 
                subject: subject 
            }),
            (response: Array<LodResourceDataItemDto>) => {
                setData(response);
            },
            (error: AxiosError) => {
                console.log("Error: " + error.message);
            },
            () => {
                setIsLoading(false);
            }
        );

        return () => {
            appDispatch(resetBreadcrumbState());
        };
    }, [locale, subject]);

    useEffect(() => {
        RestService.Get<LodResourceTemplateResponse>(
            ApiRoutes.Lod.Templates({
                localeCode: locale ?? ""
            }),
            (response: LodResourceTemplateResponse) => {
                setTemplates(response);
            },
            (error: AxiosError) => {
                console.log("Error: " + error.message);
            }
        );
    }, [locale]);

    const handleRdfFileDownload = (resourceType: LodResourceType) => {
        axios
            .get(
                ApiRoutes.Lod.ResourceFile({
                    fileType: resourceType,
                    klasisCode: id ?? "",
                    localeCode: locale ?? "",
                    subject: subject ?? undefined
                })
            )
            .then((response) => {
                const contentDisposition =
                    response.headers["content-disposition"];
                const matches = /filename=(.*);/.exec(contentDisposition);

                const fileName = matches && matches[1] ? matches[1] : "unknown";

                fileDownload(resourceType === LodResourceType.jSONLD 
                    ? JSON.stringify(response.data) 
                    : response.data, fileName);
            });
    };

    const renderValue = (item: LodResourceDataItemDto): JSX.Element | string => {
        if (!item.subject)
            return item.value;

        return (
            <Link
                component={RouterLink}
                to={{
                    pathname: klasisRoute(`lod-resources/${encodeURIComponent(id ?? "")}`),
                    search: `?${SEARCH_PARAMS.subject}=${encodeURIComponent(item.subject)}`
                }}
            >
                {item.value}
            </Link>
        )
    };

    const renderRdfFileDownloadBlock = (): JSX.Element => {
        return (
            <DownloadFileBlock>
                <Typography 
                    variant="caption"
                    sx={{marginBottom: 1, display: "block"}}
                >
                    {t(Translations.lod.label.attachedRdfFiles)}
                </Typography>
                <Stack direction="column" justifyContent="flex-start">
                    {downloadItems.map(item => (
                        <Box key={item.resourceType}>
                            <Button 
                                variant="text"
                                sx={{textTransform: "none"}}
                                onClick={() => handleRdfFileDownload(item.resourceType)}
                            >
                                <Stack 
                                    direction="row" 
                                    spacing={1} 
                                    alignItems="center"
                                >
                                    <FilePresentIcon/>
                                    <Typography>{item.title}</Typography>
                                    <Typography color="text.secondary">
                                        <SaveAltIcon/>
                                    </Typography>
                                </Stack>
                            </Button>
                        </Box>
                    ))}
                </Stack>
            </DownloadFileBlock>
        );
    };

    const renderResourceItems = (): JSX.Element => {
        return (
            <Grid container spacing={2} sx={{marginTop: 2}}>
                <HeaderItem item xs={3} sm={2}>
                    {t(Translations.lod.label.resourceName)}
                </HeaderItem>
                <HeaderItem item xs={6} sm={7}>
                    {t(Translations.lod.label.value)}
                </HeaderItem>
                <HeaderItem item xs={3} sm={3}>
                    {t(Translations.lod.label.predicate)}
                </HeaderItem>
                {data.map((item, index) => {
                    return (
                        <Fragment key={`${item.title}_${index}`}>
                            <Item item xs={3} sm={2}>
                                {item.title}
                            </Item>
                            <Item item xs={6} sm={7}>
                                {renderValue(item)}
                            </Item>
                            <Item item xs={3} sm={3}>
                                {item.predicate}
                            </Item>
                        </Fragment>
                    );
                })}
            </Grid>
        );
    };

    return (
        <PageContent
            title={resourceCode ?? id}
            showBreadcrumbs={true}
            showTitleBarCommands={true}
            isLoading={isLoading}
        >
            <Stack direction="column">
                <Box>
                    {templates?.header && parse(templates?.header)}
                </Box>
                {renderRdfFileDownloadBlock()}
                {renderResourceItems()}
                <Box>
                    {templates?.footer && parse(templates?.footer)}
                </Box>
            </Stack>
        </PageContent>
    );
}

export default LodResource;